import { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { Pagination } from '../components/Pagination';
import { Modal } from '../components/Modal';
import { NumberField } from '../components/NumberField';
import { orderService } from '../services/orderService';
import { shippingService } from '../services/shippingService';
import { fieldClass, formatDateTime, formatMoney, labelClass, parseNumber } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';
import type { Order } from '../types';

const OTHER_SHIPPING_SLUG = 'other';

type ShippingMethodOption = {
  slug: string;
  name: string;
  isActive: boolean;
  sortOrder: number;
};

function mapShippingMethod(raw: Record<string, unknown>): ShippingMethodOption {
  return {
    slug: str(raw.slug),
    name: str(raw.name, str(raw.slug)),
    isActive: raw.isActive !== false,
    sortOrder: num(raw.sortOrder ?? raw.order),
  };
}

const ORDER_STATUSES = ['pending', 'confirmed', 'shipped', 'cancelled'] as const;

const statusColors: Record<string, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'در انتظار تایید' },
  confirmed: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'تأیید شده' },
  shipped: { bg: 'bg-green-100', text: 'text-green-700', label: 'ارسال شده' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'لغو شده' },
  cancelled_user: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'لغو توسط مشتری' },
  cancelled_admin: { bg: 'bg-red-100', text: 'text-red-700', label: 'لغو توسط ادمین' },
  // legacy aliases
  processing: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'تأیید شده' },
  preparing: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'تأیید شده' },
  delivered: { bg: 'bg-green-100', text: 'text-green-700', label: 'ارسال شده' },
  refunded: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'بازپرداخت شده' },
};

const paymentStatusColors: Record<string, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'در انتظار' },
  completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'موفق' },
  paid: { bg: 'bg-green-100', text: 'text-green-700', label: 'موفق' },
  failed: { bg: 'bg-red-100', text: 'text-red-700', label: 'ناموفق' },
  refunded: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'بازپرداخت شده' },
};

const fallbackStatus = { bg: 'bg-slate-100', text: 'text-slate-700', label: 'نامشخص' };

type LooseRecord = Record<string, unknown>;

function asRecord(value: unknown): LooseRecord | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as LooseRecord) : undefined;
}

function str(value: unknown, fallback = ''): string {
  if (value == null) return fallback;
  return String(value);
}

function num(value: unknown, fallback = 0): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
  const n = parseNumber(String(value ?? ''));
  return Number.isFinite(n) ? n : fallback;
}

function normalizeOrderStatus(raw: unknown): string {
  const st = str(raw);
  switch (st) {
    case 'pending_payment':
    case 'pending':
      return 'pending';
    case 'paid':
    case 'confirmed':
    case 'preparing':
    case 'processing':
      return 'confirmed';
    case 'shipped':
    case 'delivered':
      return 'shipped';
    case 'cancelled':
    case 'returned':
      return 'cancelled';
    default:
      return st || 'pending';
  }
}

function orderStatusDisplay(order: Order | null | undefined): { bg: string; text: string; label: string } {
  const st = normalizeOrderStatus(order?.orderStatus);
  if (st === 'cancelled') {
    const by = str(order?.cancelledBy);
    if (by === 'user') return statusColors.cancelled_user;
    if (by === 'admin') return statusColors.cancelled_admin;
  }
  return statusColors[st] ?? fallbackStatus;
}

function customerName(order: Order | null): string {
  const c = asRecord(order?.customer);
  if (!c) return '—';
  const name = str(c.name).trim();
  if (name) return name;
  const combined = `${str(c.firstName)} ${str(c.lastName)}`.trim();
  if (combined) return combined;
  const receiver = str(c.receiverName).trim();
  return receiver || '—';
}

function customerPhone(order: Order | null): string {
  const c = asRecord(order?.customer);
  if (!c) return '';
  return str(c.phone || c.receiverPhone);
}

function orderAddress(order: Order | null): LooseRecord | undefined {
  return asRecord(order?.shippingAddress) ?? asRecord((order as unknown as LooseRecord | undefined)?.address);
}

function formatAddress(addr: LooseRecord | undefined): string {
  if (!addr) return '—';
  const parts = [addr.province, addr.city, addr.address, addr.postalCode]
    .map((p) => str(p).trim())
    .filter(Boolean);
  return parts.length ? parts.join('، ') : '—';
}

function itemRows(order: Order | null) {
  const items = Array.isArray(order?.items) ? order.items : [];
  return items.map((raw, index) => {
    const item = asRecord(raw) ?? {};
    const product = asRecord(item.product);
    const name = str(item.name || product?.name, '—');
    const qty = num(item.qty ?? item.quantity, 0);
    const price = num(item.price ?? product?.price);
    const total = num(item.total, price * qty);
    const id = str(item.id || item.productId || product?.id, String(index));
    const slug = str(item.slug || product?.slug);
    const extras = [str(item.color), str(item.variant)].filter(Boolean);
    const label = extras.length ? `${name} (${extras.join('، ')})` : name;
    return { id, name: label, qty, price, total, slug };
  });
}

function resolveInitialShippingSlug(order: Order, methods: ShippingMethodOption[]): string {
  const slug = str(order.shippingMethod);
  const known = new Set(methods.map((m) => m.slug));
  if (slug === OTHER_SHIPPING_SLUG) return OTHER_SHIPPING_SLUG;
  if (slug && known.has(slug)) return slug;
  if (slug) return OTHER_SHIPPING_SLUG;
  const active = methods.filter((m) => m.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  return active[0]?.slug ?? OTHER_SHIPPING_SLUG;
}

function shippingMethodLabel(slug: string, methods: ShippingMethodOption[]): string {
  if (slug === OTHER_SHIPPING_SLUG) return 'سایر';
  return methods.find((m) => m.slug === slug)?.name ?? slug;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function toDatetimeLocal(iso?: string): string {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function fromDatetimeLocal(value: string): string {
  const raw = value.trim();
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
}

export function OrdersPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPayment, setFilterPayment] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 10;

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<Order | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusValue, setStatusValue] = useState('pending');
  const [shippingMethods, setShippingMethods] = useState<ShippingMethodOption[]>([]);
  const [selectedShippingSlug, setSelectedShippingSlug] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shippingNote, setShippingNote] = useState('');
  const [shippedAtLocal, setShippedAtLocal] = useState('');
  const [shippingExpenseInput, setShippingExpenseInput] = useState('');
  const [packagingCostInput, setPackagingCostInput] = useState('');
  const [cogsTotalInput, setCogsTotalInput] = useState('');
  const storefrontBase =
    (import.meta.env.VITE_STOREFRONT_URL as string | undefined)?.replace(/\/$/, '') || 'https://evagol.ir';
  const [cancelReason, setCancelReason] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingExpenses, setSavingExpenses] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const selectableShippingMethods = useMemo(() => {
    const active = shippingMethods
      .filter((m) => m.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const currentSlug = str(detail?.shippingMethod);
    const current = shippingMethods.find((m) => m.slug === currentSlug);
    if (current && !current.isActive && !active.some((m) => m.slug === currentSlug)) {
      return [...active, current];
    }
    return active;
  }, [shippingMethods, detail?.shippingMethod]);

  const expensePreview = useMemo(() => {
    const cogs = parseNumber(cogsTotalInput);
    const shipExp = parseNumber(shippingExpenseInput);
    const pack = parseNumber(packagingCostInput);
    const final = num(detail?.finalAmount);
    const totalExpense = cogs + shipExp + pack;
    return { cogs, shipExp, pack, totalExpense, netProfit: final - totalExpense };
  }, [detail, shippingExpenseInput, packagingCostInput, cogsTotalInput]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await orderService.getOrders({
        page: currentPage,
        limit: itemsPerPage,
        status: filterStatus || undefined,
        paymentStatus: filterPayment || undefined,
        q: searchTerm.trim() || undefined,
      });
      setOrders(res.data ?? []);
      setTotalPages(Math.max(1, res.totalPages));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت سفارش‌ها');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterStatus, filterPayment, searchTerm]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await loadList();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadList]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await shippingService.list();
        if (!cancelled) {
          setShippingMethods(data.map(mapShippingMethod).filter((m) => m.slug && m.slug !== OTHER_SHIPPING_SLUG));
        }
      } catch {
        if (!cancelled) setShippingMethods([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (statusValue !== 'shipped' || !detail || selectedShippingSlug) return;
    setSelectedShippingSlug(resolveInitialShippingSlug(detail, shippingMethods));
  }, [statusValue, detail, selectedShippingSlug, shippingMethods]);

  const filteredOrders = orders;

  const closeDetail = () => {
    setDetailOpen(false);
    setDetail(null);
    setCancelReason('');
    setTrackingNumber('');
    setShippingNote('');
    setShippedAtLocal('');
    setShippingExpenseInput('');
    setPackagingCostInput('');
    setCogsTotalInput('');
    setSelectedShippingSlug('');
    setSavingStatus(false);
    setSavingExpenses(false);
    setCancelling(false);
  };

  const openDetail = async (id: string) => {
    setDetailOpen(true);
    setDetail(null);
    setCancelReason('');
    setSelectedShippingSlug('');
    setDetailLoading(true);
    try {
      const order = await orderService.getOrder(id);
      let methods = shippingMethods;
      if (methods.length === 0) {
        try {
          const data = await shippingService.list();
          methods = data.map(mapShippingMethod).filter((m) => m.slug && m.slug !== OTHER_SHIPPING_SLUG);
          setShippingMethods(methods);
        } catch {
          methods = [];
        }
      }
      setDetail(order);
      setStatusValue(normalizeOrderStatus(order?.orderStatus));
      const track = str(order?.trackingNumber);
      const note = str(order?.shippingNote);
      setTrackingNumber(track);
      setShippingNote(note);
      setShippedAtLocal(order?.shippedAt ? toDatetimeLocal(order.shippedAt) : '');
      setShippingExpenseInput(String(order?.shippingExpense ?? 0));
      setCogsTotalInput(String(order?.cogsTotal ?? 0));
      setPackagingCostInput(String(order?.packagingCost ?? 0));
      setSelectedShippingSlug(resolveInitialShippingSlug(order, methods));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت سفارش');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveExpenses = async () => {
    if (!detail?.id) return;
    const shippingExpense = parseNumber(shippingExpenseInput);
    const packagingCost = parseNumber(packagingCostInput);
    const cogsTotal = parseNumber(cogsTotalInput);
    if (shippingExpense < 0 || packagingCost < 0 || cogsTotal < 0) {
      toast.error('مبالغ هزینه نامعتبر است');
      return;
    }
    setSavingExpenses(true);
    try {
      const updated = await orderService.updateOrderExpenses(detail.id, shippingExpense, packagingCost, cogsTotal);
      setDetail(updated);
      setShippingExpenseInput(String(updated.shippingExpense ?? 0));
      setPackagingCostInput(String(updated.packagingCost ?? 0));
      setCogsTotalInput(String(updated.cogsTotal ?? 0));
      toast.success('هزینه‌های داخلی ذخیره شد');
      await loadList();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ذخیره هزینه‌ها ناموفق بود');
    } finally {
      setSavingExpenses(false);
    }
  };

  const handleSaveStatus = async () => {
    if (!detail?.id) return;
    let track = '';
    let note = '';
    let shippingMethod = '';
    let shippedAt = '';
    if (statusValue === 'shipped') {
      if (!selectedShippingSlug) {
        toast.error('روش ارسال را انتخاب کنید');
        return;
      }
      if (!shippedAtLocal.trim()) {
        toast.error('تاریخ ارسال را وارد کنید');
        return;
      }
      shippedAt = fromDatetimeLocal(shippedAtLocal);
      if (!shippedAt) {
        toast.error('تاریخ ارسال نامعتبر است');
        return;
      }
      track = trackingNumber.trim();
      note = shippingNote.trim();
      shippingMethod = selectedShippingSlug;
      if (selectedShippingSlug === OTHER_SHIPPING_SLUG) {
        if (!note && !track) {
          toast.error('توضیح ارسال یا کد رهگیری را وارد کنید');
          return;
        }
      } else if (!note && !track) {
        note = shippingMethodLabel(selectedShippingSlug, shippingMethods);
      }
    }
    setSavingStatus(true);
    try {
      await orderService.updateOrderStatus(detail.id, statusValue, {
        trackingNumber: track,
        shippingNote: note,
        shippingMethod: statusValue === 'shipped' ? shippingMethod : undefined,
        shippedAt: statusValue === 'shipped' ? shippedAt : undefined,
      });
      toast.success('وضعیت سفارش به‌روزرسانی شد');
      await loadList();
      closeDetail();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'به‌روزرسانی وضعیت ناموفق بود');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!detail?.id) return;
    const reason = cancelReason.trim();
    if (!reason) {
      toast.error('دلیل لغو را وارد کنید');
      return;
    }
    if (!confirm('آیا از لغو این سفارش مطمئن هستید؟')) return;
    setCancelling(true);
    try {
      const updated = await orderService.cancelOrder(detail.id, reason);
      setDetail(updated);
      setStatusValue(normalizeOrderStatus(updated?.orderStatus ?? 'cancelled'));
      toast.success('سفارش لغو شد');
      await loadList();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'لغو سفارش ناموفق بود');
    } finally {
      setCancelling(false);
    }
  };

  const addr = orderAddress(detail);
  const items = itemRows(detail);
  const isCancelled = normalizeOrderStatus(detail?.orderStatus) === 'cancelled';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">سفارش‌ها</h1>
          <p className="text-slate-600 mt-1">مدیریت و پیگیری سفارش‌های مشتریان</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="جستجو به شماره یا نام..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pr-10 pl-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-right"
              dir="rtl"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">تمام وضعیت‌ها</option>
            <option value="pending">در انتظار تایید</option>
            <option value="confirmed">تأیید شده</option>
            <option value="shipped">ارسال شده</option>
            <option value="cancelled">لغو شده</option>
          </select>

          <select
            value={filterPayment}
            onChange={(e) => {
              setFilterPayment(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">تمام وضعیت‌های پرداخت</option>
            <option value="pending">در انتظار</option>
            <option value="completed">موفق</option>
            <option value="failed">ناموفق</option>
            <option value="refunded">بازپرداخت شده</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="admin-table-scroll">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">ردیف</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">شماره سفارش</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">مشتری</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">مبلغ</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">وضعیت سفارش</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">وضعیت پرداخت</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">تاریخ</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                    سفارشی یافت نشد
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order, index) => {
                  const orderStatusConfig = orderStatusDisplay(order);
                  const paymentStatusConfig = paymentStatusColors[order.paymentStatus] ?? fallbackStatus;

                  return (
                    <tr
                      key={order.id}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-4 text-sm tabular-nums text-slate-500">
                        {rowNumber(index, currentPage, itemsPerPage)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-indigo-600">
                        {order.orderNumber}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{customerName(order)}</p>
                          <p className="text-xs text-slate-500">{customerPhone(order)}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        {formatMoney(order.finalAmount || 0)} تومان
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-3 py-1 text-xs rounded-full font-medium ${orderStatusConfig.bg} ${orderStatusConfig.text}`}
                        >
                          {orderStatusConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-3 py-1 text-xs rounded-full font-medium ${paymentStatusConfig.bg} ${paymentStatusConfig.text}`}
                        >
                          {paymentStatusConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        <div>{order.createdAt ? new Date(order.createdAt).toLocaleDateString('fa-IR') : '—'}</div>
                        {order.shippedAt ? (
                          <div className="text-xs text-slate-400 mt-0.5">ارسال: {formatDateTime(order.shippedAt)}</div>
                        ) : null}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          className="text-indigo-600 hover:text-indigo-700 font-medium"
                          onClick={() => void openDetail(order.id)}
                        >
                          <Eye className="h-4 w-4 inline" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-center">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </div>

      <Modal
        isOpen={detailOpen}
        onClose={closeDetail}
        title={detail?.orderNumber ? `سفارش ${detail.orderNumber}` : 'جزئیات سفارش'}
        size="xl"
        footer={
          <>
            <button
              type="button"
              onClick={closeDetail}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              بستن
            </button>
            {!isCancelled && (
              <button
                type="button"
                onClick={() => void handleSaveStatus()}
                disabled={savingStatus || detailLoading || !detail}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingStatus ? 'در حال ذخیره...' : 'ذخیره وضعیت'}
              </button>
            )}
          </>
        }
      >
        {detailLoading || !detail ? (
          <p className="text-center text-slate-500 py-8">در حال بارگذاری...</p>
        ) : (
          <div className="space-y-6 text-right" dir="rtl">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-slate-500">شماره سفارش</dt>
                <dd className="font-medium text-slate-900">{detail.orderNumber || '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">تاریخ سفارش</dt>
                <dd className="font-medium text-slate-900">
                  {detail.createdAt ? formatDateTime(detail.createdAt) : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">مشتری</dt>
                <dd className="font-medium text-slate-900">{customerName(detail)}</dd>
              </div>
              <div>
                <dt className="text-slate-500">تلفن</dt>
                <dd className="font-medium text-slate-900">{customerPhone(detail) || '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">مبلغ کل</dt>
                <dd className="font-medium text-slate-900">
                  {formatMoney(detail.totalAmount || 0)} تومان
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">تخفیف</dt>
                <dd className="font-medium text-slate-900">
                  {formatMoney(detail.discountAmount || 0)} تومان
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">کرایه ارسال (مشتری)</dt>
                <dd className="font-medium text-slate-900">
                  {formatMoney(detail.shippingCost || 0)} تومان
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">مبلغ نهایی</dt>
                <dd className="font-semibold text-slate-900">
                  {formatMoney(detail.finalAmount || 0)} تومان
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">وضعیت پرداخت</dt>
                <dd>
                  {(() => {
                    const cfg = paymentStatusColors[detail.paymentStatus] ?? fallbackStatus;
                    return (
                      <span
                        className={`inline-block px-3 py-1 text-xs rounded-full font-medium ${cfg.bg} ${cfg.text}`}
                      >
                        {cfg.label}
                      </span>
                    );
                  })()}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">وضعیت سفارش</dt>
                <dd>
                  {(() => {
                    const cfg = orderStatusDisplay(detail);
                    return (
                      <span
                        className={`inline-block px-3 py-1 text-xs rounded-full font-medium ${cfg.bg} ${cfg.text}`}
                      >
                        {cfg.label}
                      </span>
                    );
                  })()}
                </dd>
              </div>
              {isCancelled && (
                <div className="md:col-span-2 rounded-lg border border-red-100 bg-red-50/70 p-3">
                  <dt className="text-slate-500 mb-1">جزئیات لغو</dt>
                  <dd className="font-medium text-slate-900 space-y-1 text-sm">
                    <p>
                      لغوکننده:{' '}
                      {detail.cancelledBy === 'user'
                        ? 'مشتری'
                        : detail.cancelledBy === 'admin'
                          ? 'ادمین'
                          : 'نامشخص'}
                    </p>
                    <p>
                      دلیل:{' '}
                      {detail.cancelReason?.trim()
                        ? detail.cancelReason
                        : 'دلیلی ثبت نشده است'}
                    </p>
                  </dd>
                </div>
              )}
              {(detail.shippingMethod || detail.trackingNumber || detail.shippingNote || detail.shippedAt) && (
                <div className="md:col-span-2 rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
                  <dt className="text-slate-500 mb-1">اطلاعات ارسال</dt>
                  <dd className="font-medium text-slate-900 space-y-1">
                    {detail.shippedAt ? <p>تاریخ ارسال: {formatDateTime(detail.shippedAt)}</p> : null}
                    {detail.shippingMethod ? (
                      <p>
                        روش ارسال:{' '}
                        {shippingMethodLabel(str(detail.shippingMethod), shippingMethods)}
                      </p>
                    ) : null}
                    {detail.shippingNote ? <p>{detail.shippingNote}</p> : null}
                    {detail.trackingNumber ? (
                      <p dir="ltr" className="text-left font-mono">
                        کد رهگیری: {detail.trackingNumber}
                      </p>
                    ) : null}
                  </dd>
                </div>
              )}
              <div className="md:col-span-2">
                <dt className="text-slate-500">آدرس</dt>
                <dd className="font-medium text-slate-900">{formatAddress(addr)}</dd>
                {addr?.receiverName ? (
                  <p className="text-xs text-slate-500 mt-1">
                    گیرنده: {str(addr.receiverName)}
                    {addr.receiverPhone ? ` — ${str(addr.receiverPhone)}` : ''}
                  </p>
                ) : null}
              </div>
            </dl>

            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">اقلام سفارش</h3>
              {items.length === 0 ? (
                <p className="text-sm text-slate-500">آیتمی ثبت نشده است</p>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="admin-table-scroll">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-3 py-2 text-right font-medium">محصول</th>
                        <th className="px-3 py-2 text-right font-medium">تعداد</th>
                        <th className="px-3 py-2 text-right font-medium">قیمت</th>
                        <th className="px-3 py-2 text-right font-medium">جمع</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.id} className="border-b border-slate-100 last:border-0">
                          <td className="px-3 py-2">
                            {item.slug ? (
                              <a
                                href={`${storefrontBase}/products/${encodeURIComponent(item.slug)}`}
                                target="_blank"
                                rel="noreferrer"
                                className="font-medium text-indigo-700 hover:underline"
                              >
                                {item.name}
                              </a>
                            ) : (
                              item.name
                            )}
                          </td>
                          <td className="px-3 py-2">{item.qty}</td>
                          <td className="px-3 py-2">{formatMoney(item.price)} تومان</td>
                          <td className="px-3 py-2">{formatMoney(item.total)} تومان</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-amber-900">هزینه‌های داخلی (فقط ادمین)</h3>
                <p className="text-xs text-amber-800/80 mt-1">
                  این مقادیر به مشتری نمایش داده نمی‌شوند و در محاسبه سود داشبورد لحاظ می‌شوند.
                </p>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-slate-600">جمع هزینه‌ها</dt>
                  <dd className="font-semibold text-slate-900">{formatMoney(expensePreview.totalExpense)} تومان</dd>
                </div>
                <div>
                  <dt className="text-slate-600">سود خالص سفارش</dt>
                  <dd className={`font-bold ${expensePreview.netProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {formatMoney(expensePreview.netProfit)} تومان
                  </dd>
                </div>
              </dl>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>هزینه تمام‌شده کالا</label>
                  <NumberField
                    value={cogsTotalInput}
                    onChange={setCogsTotalInput}
                    placeholder="مثلاً 450000"
                  />
                </div>
                <div>
                  <label className={labelClass}>هزینه ارسال (داخلی)</label>
                  <NumberField
                    value={shippingExpenseInput}
                    onChange={setShippingExpenseInput}
                    placeholder="مثلاً 85000"
                  />
                </div>
                <div>
                  <label className={labelClass}>هزینه بسته‌بندی / کارتن</label>
                  <NumberField
                    value={packagingCostInput}
                    onChange={setPackagingCostInput}
                    placeholder="مثلاً 25000"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => void handleSaveExpenses()}
                disabled={savingExpenses || detailLoading || !detail}
                className="px-4 py-2 rounded-lg border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 disabled:opacity-50 text-sm font-medium"
              >
                {savingExpenses ? 'در حال ذخیره...' : 'ذخیره هزینه‌های داخلی'}
              </button>
            </div>

            {!isCancelled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-200">
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>تغییر وضعیت</label>
                    <select
                      className={fieldClass}
                      value={statusValue}
                      onChange={(e) => {
                        const next = e.target.value;
                        setStatusValue(next);
                        if (next === 'shipped' && !shippedAtLocal) {
                          setShippedAtLocal(toDatetimeLocal(detail?.shippedAt));
                        }
                      }}
                    >
                      {ORDER_STATUSES.filter((st) => st !== 'cancelled').map((st) => (
                        <option key={st} value={st}>
                          {statusColors[st]?.label ?? st}
                        </option>
                      ))}
                    </select>
                  </div>
                  {statusValue === 'shipped' && (
                    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <p className="text-xs font-semibold text-slate-700">جزئیات ارسال</p>
                      <div>
                        <label className={labelClass}>تاریخ ارسال</label>
                        <input
                          type="datetime-local"
                          className={fieldClass}
                          value={shippedAtLocal}
                          onChange={(e) => setShippedAtLocal(e.target.value)}
                          dir="ltr"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>روش ارسال</label>
                        <select
                          className={fieldClass}
                          value={selectedShippingSlug}
                          onChange={(e) => setSelectedShippingSlug(e.target.value)}
                        >
                          <option value="">انتخاب کنید...</option>
                          {selectableShippingMethods.map((method) => (
                            <option key={method.slug} value={method.slug}>
                              {method.name}
                              {!method.isActive ? ' (غیرفعال)' : ''}
                            </option>
                          ))}
                          <option value={OTHER_SHIPPING_SLUG}>سایر</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>کد رهگیری (اختیاری)</label>
                        <input
                          className={fieldClass}
                          value={trackingNumber}
                          onChange={(e) => setTrackingNumber(e.target.value)}
                          dir="ltr"
                          placeholder="مثلاً 1234567890"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>
                          {selectedShippingSlug === OTHER_SHIPPING_SLUG
                            ? 'توضیح ارسال'
                            : 'یادداشت ارسال (اختیاری)'}
                        </label>
                        <input
                          className={fieldClass}
                          value={shippingNote}
                          onChange={(e) => setShippingNote(e.target.value)}
                          dir="rtl"
                          placeholder={
                            selectedShippingSlug === OTHER_SHIPPING_SLUG
                              ? 'مثلاً ارسال با باربری'
                              : 'توضیح تکمیلی'
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className={labelClass}>دلیل لغو</label>
                  <textarea
                    className={fieldClass}
                    rows={2}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="در صورت لغو، دلیل را بنویسید"
                    dir="rtl"
                  />
                  <button
                    type="button"
                    onClick={() => void handleCancelOrder()}
                    disabled={cancelling}
                    className="mt-2 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 text-sm"
                  >
                    {cancelling ? 'در حال لغو...' : 'لغو سفارش'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
